// Tenantra Inc. - Guided tenant import: drop any file, confirm where each column goes, import.
import { useState, useRef, useMemo } from 'react';
import {
  UploadCloud, FileSpreadsheet, X, Loader2, CheckCircle2, ArrowRight,
  ArrowLeft, Sparkle, CircleAlert, RotateCcw, Check
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import api from '@/services/api';

const KEEP = '__extra__';
const IGNORE = '__ignore__';

const ConfidencePill = ({ value, auto }) => {
  if (!auto) return null;
  const strong = value >= 0.9;
  return (
    <span className={`text-[9px] font-semibold px-1.5 py-0.5 rounded-full ${strong ? 'bg-emerald-50 text-emerald-700' : 'bg-amber-50 text-amber-700'}`}>
      {strong ? 'Matched' : 'Best guess'}
    </span>
  );
};

// Step 1 - drop a file
function UploadStep({ onAnalyzed }) {
  const inputRef = useRef(null);
  const [drag, setDrag] = useState(false);
  const [busy, setBusy] = useState(false);

  const handleFile = async (file) => {
    if (!file) return;
    const ok = /\.(csv|xlsx|xlsm)$/i.test(file.name);
    if (!ok) { toast.error('Upload a CSV or Excel file'); return; }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await api.post('/bulk/import/analyze', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      onAnalyzed(res.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'We could not read that file');
    } finally { setBusy(false); }
  };

  return (
    <div className="space-y-3" data-testid="smart-upload-step">
      <div
        onDragEnter={(e) => { e.preventDefault(); setDrag(true); }}
        onDragLeave={(e) => { e.preventDefault(); setDrag(false); }}
        onDragOver={(e) => e.preventDefault()}
        onDrop={(e) => { e.preventDefault(); setDrag(false); handleFile(e.dataTransfer?.files?.[0]); }}
        onClick={() => !busy && inputRef.current?.click()}
        className={`relative overflow-hidden rounded-xl border border-dashed px-6 py-10 text-center cursor-pointer transition-all ${
          drag ? 'border-teal-400 bg-teal-50/60 scale-[1.01]' : 'border-slate-200 hover:border-teal-300 hover:bg-slate-50/60'
        }`}
        data-testid="smart-dropzone"
      >
        <input ref={inputRef} type="file" accept=".csv,.xlsx,.xlsm" className="hidden"
          onChange={(e) => handleFile(e.target.files?.[0])} data-testid="smart-file-input" />
        <div className={`w-11 h-11 mx-auto mb-3 rounded-xl flex items-center justify-center transition-colors ${drag ? 'bg-teal-500 text-white' : 'bg-slate-100 text-slate-400'}`}>
          {busy ? <Loader2 className="w-5 h-5 animate-spin" /> : <UploadCloud className="w-5 h-5" />}
        </div>
        <p className="text-sm font-medium text-slate-700">
          {busy ? 'Reading your file' : 'Drop a rent roll or tenant list here'}
        </p>
        <p className="text-xs text-slate-400 mt-1">
          {busy ? 'This only takes a moment' : 'CSV or Excel from any system, any column names'}
        </p>
      </div>
      <div className="flex items-start gap-2 text-[11px] text-slate-500 bg-slate-50 border border-slate-100 rounded-lg px-3 py-2">
        <Sparkle className="w-3.5 h-3.5 text-teal-500 flex-shrink-0 mt-0.5" />
        <span>Columns are matched to your tenant fields automatically. You confirm anything unclear before a single record is saved.</span>
      </div>
    </div>
  );
}

// Step 2 - confirm where each column goes
function MapStep({ analysis, onBack, onDone }) {
  const [decisions, setDecisions] = useState(() =>
    Object.fromEntries(analysis.columns.map((c) => [c.source, c.target || '']))
  );
  const [remember, setRemember] = useState(true);
  const [busy, setBusy] = useState(false);

  const grouped = useMemo(() => {
    const g = {};
    analysis.fields.forEach((f) => { (g[f.group] = g[f.group] || []).push(f); });
    return g;
  }, [analysis.fields]);

  const usedFieldKeys = useMemo(() => {
    const counts = {};
    Object.values(decisions).forEach((v) => { if (v && v !== KEEP && v !== IGNORE) counts[v] = (counts[v] || 0) + 1; });
    return counts;
  }, [decisions]);

  const nameMapped = Object.values(decisions).includes('full_name');
  const mappedCount = Object.values(decisions).filter((v) => v && v !== IGNORE).length;
  const pendingCount = analysis.columns.filter((c) => !decisions[c.source]).length;
  const dupField = Object.entries(usedFieldKeys).find(([, n]) => n > 1);

  const setOne = (source, value) => setDecisions((d) => ({ ...d, [source]: value }));

  const run = async () => {
    if (!nameMapped) { toast.error('Choose which column holds the tenant name'); return; }
    if (dupField) { toast.error('Two columns point to the same field. Adjust one of them.'); return; }
    setBusy(true);
    try {
      const mapping = Object.entries(decisions).map(([source, target]) => ({ source, target: target || null }));
      const res = await api.post('/bulk/import/commit', { session_id: analysis.session_id, mapping, remember });
      onDone(res.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Import could not be completed');
    } finally { setBusy(false); }
  };

  return (
    <div className="space-y-3" data-testid="smart-map-step">
      {/* Summary strip */}
      <div className="flex flex-wrap items-center gap-x-5 gap-y-1 bg-white border border-slate-200 rounded-lg px-4 py-2.5">
        <div className="flex items-center gap-2">
          <FileSpreadsheet className="w-4 h-4 text-teal-600" />
          <span className="text-xs font-medium text-slate-700 truncate max-w-[180px]">{analysis.filename}</span>
        </div>
        <span className="text-xs text-slate-400">{analysis.total_rows} rows</span>
        <span className="text-xs text-slate-500"><span className="font-semibold text-slate-800">{mappedCount}</span> of {analysis.columns.length} columns ready</span>
        {analysis.remembered && (
          <span className="flex items-center gap-1 text-[11px] text-teal-700 bg-teal-50 rounded-full px-2 py-0.5">
            <RotateCcw className="w-3 h-3" /> Using your saved layout
          </span>
        )}
        {pendingCount > 0 && (
          <span className="flex items-center gap-1 text-[11px] text-amber-700" data-testid="smart-pending-count">
            <CircleAlert className="w-3 h-3" /> {pendingCount} need{pendingCount === 1 ? 's' : ''} a choice
          </span>
        )}
      </div>

      {/* Mapping table */}
      <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
        <div className="grid grid-cols-12 gap-3 px-4 py-2 bg-slate-50 border-b border-slate-200">
          <div className="col-span-5 text-[10px] font-semibold text-slate-400 uppercase tracking-widest">Column in your file</div>
          <div className="col-span-7 text-[10px] font-semibold text-slate-400 uppercase tracking-widest">Where it goes</div>
        </div>
        <div className="divide-y divide-slate-100 max-h-[42vh] overflow-y-auto">
          {analysis.columns.map((c) => {
            const val = decisions[c.source];
            const unset = !val;
            return (
              <div key={c.source} className="grid grid-cols-12 gap-3 px-4 py-2.5 items-center hover:bg-slate-50/60 transition-colors" data-testid={`map-row-${c.source}`}>
                <div className="col-span-5 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="text-xs font-medium text-slate-800 truncate">{c.source}</span>
                    <ConfidencePill value={c.confidence} auto={c.auto} />
                  </div>
                  {c.samples?.length > 0 && (
                    <p className="text-[10px] text-slate-400 truncate mt-0.5">e.g. {c.samples.slice(0, 2).join(', ')}</p>
                  )}
                </div>
                <div className="col-span-7 flex items-center gap-2">
                  <div className="relative flex-1">
                    <select
                      value={val}
                      onChange={(e) => setOne(c.source, e.target.value)}
                      className={`w-full h-8 text-xs rounded-md border bg-white pl-2.5 pr-7 appearance-none cursor-pointer transition-colors focus:outline-none focus:ring-1 focus:ring-teal-400 ${
                        unset ? 'border-amber-300 text-amber-700' : 'border-slate-200 text-slate-700'
                      }`}
                      data-testid={`map-select-${c.source}`}
                    >
                      <option value="">Choose where this goes</option>
                      {Object.entries(grouped).map(([group, fields]) => (
                        <optgroup key={group} label={group}>
                          {fields.map((f) => (
                            <option key={f.key} value={f.key} disabled={usedFieldKeys[f.key] && val !== f.key}>
                              {f.label}{f.required ? ' (required)' : ''}{usedFieldKeys[f.key] && val !== f.key ? ' - in use' : ''}
                            </option>
                          ))}
                        </optgroup>
                      ))}
                      <optgroup label="Other">
                        <option value={KEEP}>Keep as an imported detail</option>
                        <option value={IGNORE}>Do not import this column</option>
                      </optgroup>
                    </select>
                    <ArrowRight className="w-3.5 h-3.5 text-slate-300 absolute right-2 top-1/2 -translate-y-1/2 rotate-90 pointer-events-none" />
                  </div>
                  {val && val !== IGNORE && <Check className="w-3.5 h-3.5 text-emerald-500 flex-shrink-0" />}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {!nameMapped && (
        <p className="text-[11px] text-amber-700 bg-amber-50 border border-amber-100 rounded-lg px-3 py-2" data-testid="smart-name-hint">
          Point one column to Tenant name to continue. Anything we cannot place is kept safely under imported details.
        </p>
      )}

      <div className="flex items-center justify-between pt-1">
        <label className="flex items-center gap-2 text-xs text-slate-600 cursor-pointer select-none">
          <input type="checkbox" checked={remember} onChange={(e) => setRemember(e.target.checked)} className="rounded border-slate-300 text-teal-600 focus:ring-teal-400" data-testid="smart-remember" />
          Remember this layout for next time
        </label>
        <div className="flex items-center gap-2">
          <Button onClick={onBack} variant="outline" className="h-8 text-xs" data-testid="smart-back-btn">
            <ArrowLeft className="w-3.5 h-3.5 mr-1" /> Back
          </Button>
          <Button onClick={run} disabled={busy || !nameMapped} className="h-8 text-xs bg-slate-800 hover:bg-slate-900 text-white border-0" data-testid="smart-import-btn">
            {busy ? <><Loader2 className="w-3.5 h-3.5 animate-spin mr-1.5" /> Importing</> : <>Import {analysis.total_rows} rows <ArrowRight className="w-3.5 h-3.5 ml-1" /></>}
          </Button>
        </div>
      </div>
    </div>
  );
}

// Step 3 - outcome
function ResultStep({ result, onRestart }) {
  const warn = result.errors?.length > 0;
  return (
    <div className="space-y-4" data-testid="smart-result-step">
      <div className="text-center py-4">
        <div className={`w-12 h-12 mx-auto mb-3 rounded-full flex items-center justify-center ${warn ? 'bg-amber-50' : 'bg-emerald-50'}`}>
          {warn ? <CircleAlert className="w-6 h-6 text-amber-500" /> : <CheckCircle2 className="w-6 h-6 text-emerald-500" />}
        </div>
        <h3 className="text-sm font-semibold text-slate-800">
          {result.imported > 0 ? `${result.imported} ${result.imported === 1 ? 'tenant' : 'tenants'} imported` : 'Nothing was imported'}
        </h3>
        <p className="text-xs text-slate-500 mt-0.5">from {result.total_rows} rows in your file</p>
      </div>

      <div className="grid grid-cols-3 gap-3">
        {[['Imported', result.imported, 'text-emerald-600'], ['Skipped', result.skipped, 'text-amber-600'], ['Total rows', result.total_rows, 'text-slate-700']].map(([label, n, cls]) => (
          <div key={label} className="bg-slate-50 rounded-lg p-3 text-center">
            <div className={`text-lg font-bold ${cls}`}>{n}</div>
            <div className="text-[10px] text-slate-500">{label}</div>
          </div>
        ))}
      </div>

      {warn && (
        <div>
          <h4 className="text-[10px] font-semibold text-slate-400 uppercase tracking-widest mb-1.5">Notes</h4>
          <div className="space-y-1.5 max-h-44 overflow-y-auto" data-testid="smart-result-errors">
            {result.errors.map((e, i) => (
              <div key={i} className="flex items-start gap-2 bg-slate-50 border border-slate-100 rounded px-2.5 py-1.5">
                <CircleAlert className="w-3.5 h-3.5 text-amber-400 flex-shrink-0 mt-0.5" />
                <p className="text-[11px] text-slate-600"><span className="font-medium">Row {e.row}</span> {e.message}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      <Button onClick={onRestart} className="w-full h-8 text-xs bg-slate-800 hover:bg-slate-900 text-white border-0" data-testid="smart-restart-btn">
        Import another file
      </Button>
    </div>
  );
}

export function SmartImportWizard() {
  const [analysis, setAnalysis] = useState(null);
  const [result, setResult] = useState(null);
  const step = result ? 'result' : analysis ? 'map' : 'upload';

  const steps = ['Upload', 'Confirm', 'Done'];
  const activeIdx = step === 'upload' ? 0 : step === 'map' ? 1 : 2;

  return (
    <div className="bg-white border border-slate-200 rounded-lg p-4" data-testid="smart-import">
      <div className="flex items-center gap-2 mb-3">
        {steps.map((label, i) => (
          <div key={label} className="flex items-center gap-2">
            <div className={`flex items-center gap-1.5 text-[11px] font-medium ${i <= activeIdx ? 'text-teal-700' : 'text-slate-300'}`}>
              <span className={`w-5 h-5 rounded-full flex items-center justify-center text-[10px] ${i < activeIdx ? 'bg-teal-600 text-white' : i === activeIdx ? 'bg-teal-100 text-teal-700' : 'bg-slate-100 text-slate-400'}`}>
                {i < activeIdx ? <Check className="w-3 h-3" /> : i + 1}
              </span>
              {label}
            </div>
            {i < steps.length - 1 && <div className={`w-6 h-px ${i < activeIdx ? 'bg-teal-400' : 'bg-slate-200'}`} />}
          </div>
        ))}
      </div>

      {step === 'upload' && <UploadStep onAnalyzed={setAnalysis} />}
      {step === 'map' && <MapStep analysis={analysis} onBack={() => setAnalysis(null)} onDone={setResult} />}
      {step === 'result' && <ResultStep result={result} onRestart={() => { setAnalysis(null); setResult(null); }} />}
    </div>
  );
}
